import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppBranding } from '../../../../config/branding';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailableRevenueStationReport,
  RequestReportInterFace,
} from '../../../../interface/reportrevenueLocation.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchRevenueReportStation,
  updateReportFilters,
  fetchListFilters,
  setReportFiltersData,
  setRevenueStationReportUpdate,
  setSortData,
} from '../../../../redux/slices/ReportRevenueLocationSlice';
import { resetReportUpdateFlag } from '../../../../redux/slices/ReportSlice';
import CustomDateRangePicker, {
  calculateDateRange,
  normalizePresetKey,
} from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import { useTableSidePanel } from '../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import LocationSidePanel from '../../../../shared-components/table-side-panel/LocationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import { getDaysBetween } from '../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatDuration from '../../../../shared-functions/FormatDuration';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import reportApi from '../../../../utils/api/reports';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter: any;
  rightTopContent?: React.ReactElement;
}

/**
 * Revenue Report Table for Station
 * @param {ReportTableProperties} properties - The properties
 * @returns {React.ReactElement} The rendered component
 */
function RevenueReportTableStation(properties: ReportTableProperties) {
  const branding = useAppBranding();
  const { t } = useTranslation('report');
  const { leftTopContent, preferenceFilter, rightTopContent } = properties;
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const previousAccountId = useRef<string | undefined>(undefined);
  const {
    sortBy,
    sortField,
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isRevenueStationReportUpdate,
    isFilterUpdated,
  } = useAppSelector((state) => state.reportRevenueLocation);
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(() => {
    const defaultRange = calculateDateRange(normalizePresetKey('last14Days'), 'future');
    return {
      startDate: defaultRange?.startDate || '',
      endDate: defaultRange?.endDate || '',
    };
  });
  const previousDateRange = useRef<{ startDate: string; endDate: string }>({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  /**
   * Clears the date range values.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          onClear={clearDateRange}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
          disableDates="future"
        />
      </div>
    );
  };

  /**
   * Fetches energy report data for the selected facility.
   * @param {RequestReportInterFace} data - The request parameters for fetching the report.
   */
  const fetchData = useCallback(
    (data: RequestReportInterFace) => {
      if (data.startDate && data.endDate && getDaysBetween(data.startDate, data.endDate) > 31) {
        dispatch(
          setNotificationMessage({
            message: t('dateRangeLimitError'),
            status: true,
            type: 'error',
            code: 400,
          }),
        );
        return;
      }
      dispatch(
        fetchRevenueReportStation({
          ...data,
          accountCustomId: data.accountCustomId || reportsData?.accountId,
        }),
      );
    },
    [dispatch, reportsData?.accountId, t],
  );
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortField,
      sortBy,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };
  const {
    isEditMode,
    handleEditModeChange,
    handleSidePanelClose,
    renderSidePanelTopLeftContent,
    handleOpenStationSidePanel,
  } = useTableSidePanel({
    onRefresh: handleRefresh,
    sessions: reports,
    disableEdit: true,
    disableDelete: true,
  });

  const column = [
    {
      label: t('sNo'),
      accessorKey: 's_no' as keyof AvailableRevenueStationReport,
      /**
       * S.No view
       * @param {AvailableRevenueStationReport} _report - The report
       * @param {number} index - The index
       * @returns {React.ReactElement} The rendered element
       */
      customData: (_report: AvailableRevenueStationReport, index: number) => (
        <div className="table-row-each-data align-center">{(page - 1) * limit + index + 1}</div>
      ),
      columnWidth: 50,
    },
    {
      label: t('stationInfo'),
      accessorKey: 'chargingStationName' as keyof AvailableRevenueStationReport,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: (
        <LocationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      sortAllow: false,
      columnWidth: 204,
      /**
       * Station info view
       * @param {AvailableRevenueStationReport} report - The report
       * @returns {React.ReactElement} The rendered element
       */
      customData: (report: AvailableRevenueStationReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span
              role="presentation"
              className="hyperlink"
              onClick={(event) => {
                event.stopPropagation();
                handleOpenStationSidePanel(report);
              }}>
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="presentation">{report.chargingStationLocation}</span>
          </div>
        </div>
      ),
    },

    {
      label: t('noOfDevice'),
      accessorKey: 'deviceCount' as keyof AvailableRevenueStationReport,
      sortAllow: true,
    },
    {
      label: t('totalRevenue'),
      accessorKey: 'totalRevenue' as keyof AvailableRevenueStationReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param {AvailableRevenueStationReport} row - The row
       * @returns {React.ReactElement} The result
       */
      customData: (row: AvailableRevenueStationReport) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(row?.totalRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('sessionCount'),
      accessorKey: 'sessionCount' as keyof AvailableRevenueStationReport,
      sortAllow: true,
    },
    {
      label: t('duration'),
      accessorKey: 'durationSeconds' as keyof AvailableRevenueStationReport,
      sortAllow: true,
      /**
       * Formatted duration
       * @param {AvailableRevenueStationReport} row - The row
       * @returns {React.ReactElement} The result
       */
      customData: (row: AvailableRevenueStationReport) => (
        <span>
          {FormatDuration(row?.durationSeconds || (row as any)?.duration, { formatBy: 'sec' })}
        </span>
      ),
    },
    {
      label: t('createdAt', { defaultValue: 'Created At' }),
      accessorKey: 'createdAt' as keyof AvailableRevenueStationReport,
      sortAllow: true,
      columnWidth: 150,
      /**
       * Created at date formatter
       * @param {AvailableRevenueStationReport} row - The row
       * @returns {React.ReactElement} The result
       */
      customData: (row: AvailableRevenueStationReport) => (
        <span>
          {ConvertUTCtoDateTime(
            row?.createdAt || (row as any)?.created || (row as any)?.created_at || '',
          ) || '-'}
        </span>
      ),
    },
    {
      label: t('lastUpdated', { defaultValue: 'Last Updated' }),
      accessorKey: 'updatedAt' as keyof AvailableRevenueStationReport,
      sortAllow: true,
      columnWidth: 150,
      /**
       * Updated at date formatter
       * @param {AvailableRevenueStationReport} row - The row
       * @returns {React.ReactElement} The result
       */
      customData: (row: AvailableRevenueStationReport) => (
        <span>
          {ConvertUTCtoDateTime(
            row?.updatedAt || (row as any)?.lastUpdated || (row as any)?.updated_at || '',
          ) || '-'}
        </span>
      ),
    },
  ];

  /**
   * Current columns for Revenue Station Report export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('stationName'), accessorKey: 'chargingStationName' },
    { label: t('stationLocation'), accessorKey: 'chargingStationLocation' },
    { label: t('noOfDevice'), accessorKey: 'deviceCount' },
    { label: t('totalRevenue'), accessorKey: 'totalRevenue' },
    { label: t('sessionCount'), accessorKey: 'sessionCount' },
    { label: t('duration'), accessorKey: 'durationSeconds' },
    { label: t('createdAt', { defaultValue: 'Created At' }), accessorKey: 'createdAt' },
    { label: t('lastUpdated', { defaultValue: 'Last Updated' }), accessorKey: 'updatedAt' },
  ];

  const exportCurrentColumns = currentColumns.filter((col) => {
    // Always show serial number
    if (col.accessorKey === '__serial__') return true;

    // automatically hide related columns
    const isParentHidden = hiddenColumns.includes('chargingStationName');

    if (isParentHidden) {
      if (
        col.accessorKey === 'chargingStationName' ||
        col.accessorKey === 'date' ||
        col.accessorKey === 'chargingStationLocation'
      ) {
        return false;
      }
    }

    // Default hide logic
    return !hiddenColumns.includes(col.accessorKey);
  });

  /**
   * Export data function
   * @param {string} type - The type of export ('csv' or 'pdf')
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);

    const exportParameters: any = {
      ...queryParameters,
      startDate: dateRange.startDate || reportsData.reportFilter.startDate,
      endDate: dateRange.endDate || reportsData.reportFilter.endDate,
    };

    if (sortBy) {
      exportParameters.sortBy = sortBy;
      exportParameters.sortField = sortField;
    }

    reportApi
      .fetchRevenueStationListExport({
        ...exportParameters,
        accountCustomId: reportsData?.accountId,
      })
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: AvailableRevenueStationReport, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'date':
                return row?.date || '-';

              case 'chargingStationName':
                return row?.chargingStationName || '-';

              case 'chargingStationLocation':
                return row?.chargingStationLocation || '-';

              case 'deviceCount':
                return row?.deviceCount || '0';

              case 'totalRevenue':
                return row?.totalRevenue
                  ? `${ToFixedValue(row.totalRevenue)} ${branding.text.currency}`
                  : `0.00 ${branding.text.currency}`;

              case 'sessionCount':
                return row?.sessionCount ?? (row as any)?.totalSessions ?? '0';

              case 'durationSeconds':
                return row?.durationSeconds || (row as any)?.duration
                  ? FormatDuration(Number(row?.durationSeconds || (row as any)?.duration))
                  : '-';

              case 'createdAt':
                return (row as any).createdAt ? ConvertUTCtoDateTime((row as any).createdAt) : '-';

              case 'updatedAt':
                return (row as any).updatedAt ? ConvertUTCtoDateTime((row as any).updatedAt) : '-';

              default:
                return '-';
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      startDate: dateRange.startDate || reportsData.reportFilter.startDate,
      endDate: dateRange.endDate || reportsData.reportFilter.endDate,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortBy,
      sortField,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      year: reportsData.reportFilter.year,
      page,
      limit,
    });
  };
  /**
   * Function to handle ApiRequest when page or limit changes
   * @param {RequestReportInterFace} value - The request parameters
   */
  const handleApiRequest = (value: RequestReportInterFace) => {
    if (reportsData?.accountId) {
      fetchData({
        ...GetParametersFromCommonFilters(filters),
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...value,
        sortBy,
        sortField,
      });
    }
  };
  /**
   * Fetches revenue report data with query parameters.
   * @param {string} [customAccountId] - The account custom ID.
   * @param {boolean} [resetFilters] - Whether to reset filters.
   */
  const fetchReportData = useCallback(
    (customAccountId?: string, resetFilters = false) => {
      const accountCustomId = customAccountId || reportsData?.accountId;
      const queryParameters = resetFilters ? {} : GetParametersFromCommonFilters(filters);
      if (accountCustomId) {
        fetchData({
          ...queryParameters,
          accountCustomId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          page: DefaultApiLimit.page,
          limit: DefaultApiLimit.limit,
        });
      }
    },
    [filters, dateRange.startDate, dateRange.endDate, reportsData?.accountId, fetchData],
  );

  useEffect(() => {
    const isDateRangeChanged =
      previousDateRange.current.startDate !== dateRange.startDate ||
      previousDateRange.current.endDate !== dateRange.endDate;

    if (
      isFilterUpdated ||
      isRevenueStationReportUpdate ||
      reportsData.isReportUpdated ||
      isDateRangeChanged
    ) {
      if (isDateRangeChanged) {
        previousDateRange.current = dateRange;
      }
      fetchReportData();
      if (reportsData.isReportUpdated) {
        dispatch(resetReportUpdateFlag());
      }
      if (isRevenueStationReportUpdate) {
        dispatch(setRevenueStationReportUpdate(false));
      }
    }
  }, [
    fetchReportData,
    isFilterUpdated,
    isRevenueStationReportUpdate,
    reportsData.isReportUpdated,
    dateRange.startDate,
    dateRange.endDate,
    dispatch,
  ]);

  useEffect(() => {
    if (
      preferenceFilter.pageName === 'revenueReport' &&
      filters.length > 1 &&
      reportsData.reportByType === 'station'
    ) {
      const setFilter = filters.map((filter) => {
        let returnValue = filter;
        const single = preferenceFilter.filter?.find(
          (preference: any) => preference.field === filter.key,
        );
        if (single && single?.value.length > 0) {
          returnValue = {
            ...returnValue,
            value: single?.value,
            applyFilter: true,
            valueToShow: returnValue?.listOfValue
              ?.filter((show) => single?.value?.includes(show.value))
              .map((show) => show.label),
          };
        } else {
          returnValue = {
            ...returnValue,
            value: [],
            applyFilter: false,
            valueToShow: [],
          };
        }
        return returnValue;
      });
      dispatch(setReportFiltersData(setFilter));
      const queryParameters = GetParametersFromCommonFilters(setFilter);
      fetchData({
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page: DefaultApiLimit.page,
        limit,
        accountCustomId: reportsData?.accountId,
      });
    }
  }, [preferenceFilter]);

  useEffect(() => {
    if (reportsData?.accountId) {
      if (previousAccountId.current !== reportsData.accountId) {
        previousAccountId.current = reportsData.accountId;
        dispatch(fetchListFilters({ accountCustomId: reportsData.accountId }));
        fetchReportData(reportsData.accountId, true);
      }
    }
  }, [reportsData?.accountId, fetchReportData, dispatch]);

  return (
    <div className="report-management-wrap">
      <div className="table-container">
        <CustomTable
          disablePagination={false}
          loading={loading}
          limit={limit}
          header={column}
          data={reports}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={handleApiRequest}
          version="v2"
          height={370}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportData={reports}
          exportLoader={exportLoader}
          disableHeader={false}
          filters={filters}
          filtersToDispatch={updateReportFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          leftTopContent={leftTopContent}
          refershCurrentPages={ReferenceCurrentPages}
          sizeChangeButtonRequired
          onHiddenColumnsChange={setHiddenColumns}
          onSidePanelClose={() => {
            handleSidePanelClose();
          }}
          sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
          rightTopContent={
            <div className="table-bottom-button-list">
              {renderDateFilters()}
              <div className="right-action">{rightTopContent}</div>
            </div>
          }
          onClearFilterChange={() => {
            clearDateRange();
          }}
        />
      </div>
    </div>
  );
}

export default RevenueReportTableStation;
